import { ArrowForward } from "@mui/icons-material";
import { Box, IconButton, List, ListItem, ListItemButton, ListItemText } from "@mui/material";
import { categoryDateRange, displayClusterType, displayDateLoose, displayPageType } from "Backend";
import theme from "theme";
import { P } from "theme/typography";
import { LensCaseFindings, SectionHitCounts } from "generated/graphql";

const renderSectionHitCounts = (hitCounts: SectionHitCounts): string[] => {
  const pageTypes = hitCounts.pageTypes.map(
    (pageTypeCount) => `${displayPageType(pageTypeCount.pageType)} (${pageTypeCount.count})`
  );
  const clusterTypes = hitCounts.clusterTypes.map(
    (clusterTypeCount) => `${displayClusterType(clusterTypeCount.clusterType)} (${clusterTypeCount.count})`
  );
  const labelNames = hitCounts.labels.map((labelCount) => `${labelCount.labelName} (${labelCount.count})`);
  return [...labelNames, ...pageTypes, ...clusterTypes];
};

interface LensCategoryProps {
  onClick: (categoryIndex: number) => void;
  categoryIndex: number;
  title: string;
  dateFormatted: string;
  recordTypes: string[];
  pendo: string;
}

export const LensCategory = ({
  onClick,
  categoryIndex,
  dateFormatted,
  recordTypes,
  title,
  pendo,
}: LensCategoryProps) => {
  return (
    <ListItem
      data-pendo={pendo}
      sx={{ borderBottom: `1px solid ${theme.palette.grey[100]}`, pl: 0 }}
      secondaryAction={
        <IconButton edge="end" onClick={() => onClick(categoryIndex)}>
          <ArrowForward />
        </IconButton>
      }>
      <ListItemButton onClick={() => onClick(categoryIndex)}>
        <ListItemText
          primary={title}
          secondary={
            <Box sx={{ display: "flex", flexDirection: "column", mt: 1 }}>
              <P variant="body4">{dateFormatted}</P>
              <P variant="body5">{recordTypes.join(", ")}</P>
            </Box>
          }
        />
      </ListItemButton>
    </ListItem>
  );
};

interface LensCategoriesProps {
  findings: LensCaseFindings;
  loadingHits: boolean;
  hideDuplicates: boolean;
  lensHitsFullCount: number;
  expandedCategoryIndex: number | undefined;
  onCategoryClicked: (categoryIndex: number) => void;
  onAllHitsClicked: () => void;
}

const LensCategories = ({
  findings,
  loadingHits,
  hideDuplicates,
  lensHitsFullCount,
  expandedCategoryIndex,
  onCategoryClicked,
  onAllHitsClicked,
}: LensCategoriesProps) => {
  const summaries = findings.findings;

  return (
    <List sx={{ p: 0 }}>
      {summaries.length > 1 && (
        <LensCategory
          pendo="lens-category-all-hits"
          onClick={onAllHitsClicked}
          categoryIndex={-1}
          title={`All Hits (${findings.totalCount})`}
          dateFormatted={`${displayDateLoose(findings.minDate)} - ${displayDateLoose(findings.maxDate)}`}
          recordTypes={renderSectionHitCounts(findings.totalSectionCounts)}
        />
      )}

      {summaries.map((finding, idx) => {
        const recordTypes = renderSectionHitCounts(finding.sectionHitCounts);
        const expanded = expandedCategoryIndex === idx;
        const dateFormatted = categoryDateRange(finding);
        const countToShow = finding.count;
        const distinctClarification = expanded && !loadingHits && hideDuplicates ? `${lensHitsFullCount} distinct` : "";
        const findingCountString = distinctClarification ? distinctClarification : countToShow;

        return (
          <LensCategory
            pendo="lens-category"
            key={idx}
            onClick={onCategoryClicked}
            categoryIndex={idx}
            title={`${finding.ruleType.subtype} - ${finding.ruleType.description} 
        (${findingCountString})`}
            dateFormatted={dateFormatted}
            recordTypes={recordTypes}
          />
        );
      })}
    </List>
  );
};

export default LensCategories;
